import type { Phase } from "@logic/lib/design"
import { PHASES } from "@logic/lib/design"
import {
  atom,
  DefaultValue,
  selector,
  selectorFamily,
  useRecoilTransaction_UNSTABLE,
} from "recoil"

import { designLocalState } from "~/containers/Proposal/state/designs/design-state"
import { userHasRequiredPermissionsState } from "~/containers/Proposal/state/user/user-state"

import { getNextPhaseBlockers, preformatBlocker } from "./blockers"
import {
  PANEL_ACTIVATION_ALLOWED_PERMISSIONS,
  SERVICE_BUREAU_PHASES,
  SERVICE_BUREAU_PHASE_ALLOWED_PERMISSIONS,
} from "./const"
import { entryOperationsFor, regressionOperationsFor } from "./transition"
import type { TransactionOperation } from "../elements/transactions/transactors"
import { readonlyTransactors } from "../elements/transactions/transactors"
import { FAVORITE_TOOL_PER_PHASE } from "../tool"

export * from "./blockers"
export * from "./const"
export * from "./transition"

export const phaseState = selector<Phase>({
  key: `phase`,
  get: ({ get }) => get(designLocalState).phase,
  set: (transactors, newValue) => {
    return newValue instanceof DefaultValue
      ? console.warn(`tried to reset phaseState; this is not possible`)
      : enterPhase(transactors, newValue)
  },
})

export const attemptedPhaseTransitionState = atom<Phase | null>({
  key: `attemptedPhaseTransition`,
  default: null,
})
export const phasesToResetState = atom<Phase[]>({
  key: `phasesToReset`,
  default: [],
})

export const setPhasesToReset: TransactionOperation<Phase | null> = (
  transactors,
  phase,
) => {
  const { get, set } = transactors
  const { phase: currentPhase } = get(designLocalState)
  if (phase === null) return
  if (phase === currentPhase) {
    set(phasesToResetState, [])
    return
  }
  const phasesToReset = PHASES.slice(
    PHASES.indexOf(phase) + 1,
    PHASES.indexOf(currentPhase) + 1,
  ).reverse()
  set(phasesToResetState, phasesToReset)
}
export const attemptPhaseTransition: TransactionOperation<Phase> = (
  transactors,
  phase,
) => {
  const { set, get } = transactors
  setPhasesToReset(transactors, phase)
  const phasesToReset = get(phasesToResetState)
  const checkForRegressiveOperations = Object.values(phasesToReset).some(
    (phase) => regressionOperationsFor[phase].desc !== null,
  )
  if (checkForRegressiveOperations && phasesToReset.length > 0) {
    set(attemptedPhaseTransitionState, phase)
  } else {
    enterPhase(transactors, phase)
  }
}
export const useAttemptPhaseTransition = (): ((phase: Phase) => void) =>
  useRecoilTransaction_UNSTABLE(
    (transactors) => (phase: Phase) =>
      attemptPhaseTransition(transactors, phase),
  )

export const cancelPhaseTransition: TransactionOperation = ({ set }) => {
  set(phasesToResetState, [])
  set(attemptedPhaseTransitionState, null)
}
export const useCancelPhaseTransition = (): VoidFunction =>
  useRecoilTransaction_UNSTABLE(
    (transactors) => () => cancelPhaseTransition(transactors),
  )

export const enterPhase: TransactionOperation<Phase> = (transactors, phase) => {
  const { set, get } = transactors
  const phasesToReset = get(phasesToResetState)
  if (phasesToReset.length > 0) {
    for (const phaseToReset of phasesToReset) {
      const { operation } = regressionOperationsFor[phaseToReset]
      operation(transactors)
    }
    set(phasesToResetState, [])
    set(attemptedPhaseTransitionState, null)
  }
  set(designLocalState, (current) => ({
    ...current,
    phase,
    tool: FAVORITE_TOOL_PER_PHASE[phase],
    completedAt: phase === `Panel Activation` ? new Date() : null,
  }))
  entryOperationsFor[phase].forEach((operation) => operation(transactors))
}
export const useEnterPhase = (): ((phase: Phase) => void) =>
  useRecoilTransaction_UNSTABLE(
    (transactors) => (phase: Phase) => enterPhase(transactors, phase),
  )

export const enterNextPhase: TransactionOperation = (transactors) => {
  const { get } = transactors
  const { phase: currentPhase } = get(designLocalState)
  const phaseBlockers = getNextPhaseBlockers(transactors)
  if (phaseBlockers.length === 0) {
    const nextPhase = PHASES[PHASES.indexOf(currentPhase) + 1]
    enterPhase(transactors, nextPhase)
  } else {
    console.warn(`Tried to go to next phase, but failed due to blockers...`)
    phaseBlockers.forEach((blocker) => console.warn(preformatBlocker(blocker)))
  }
}

export const useEnterNextPhase = (): VoidFunction =>
  useRecoilTransaction_UNSTABLE(
    (transactors) => () => enterNextPhase(transactors),
  )

export const checkWhetherAfterPhase: TransactionOperation<Phase, boolean> = (
  transactors,
  phase,
) => {
  const { get } = transactors
  const { phase: currentPhase } = get(designLocalState)

  return PHASES.indexOf(phase) < PHASES.indexOf(currentPhase)
}

export const areWeAfterPhase = selectorFamily<boolean, Phase>({
  key: `areWeAfterPhase`,
  get:
    (phase) =>
    ({ get }) =>
      checkWhetherAfterPhase(readonlyTransactors(get), phase),
})

export const userHasRequiredPermissionsForPhaseState = selectorFamily<
  boolean,
  Phase
>({
  key: `userHasRequiredPermissionsForPhase`,
  get:
    (phase) =>
    ({ get }) => {
      const allowedPermissions = SERVICE_BUREAU_PHASES.includes(phase)
        ? SERVICE_BUREAU_PHASE_ALLOWED_PERMISSIONS
        : PANEL_ACTIVATION_ALLOWED_PERMISSIONS

      return get(userHasRequiredPermissionsState(allowedPermissions))
    },
})
